/** Пончик расходов. Проценты снаружи, значок категории внутри дольки (MA-FR-6).
 *
 * ПОЧЕМУ ПРОЦЕНТЫ СНАРУЖИ. Внутри они помещаются только у крупных долек: у
 * шестипроцентной подпись не влезает в толщину кольца, и половина пирога стоит
 * без чисел. Снаружи места столько, сколько нужно, и подпись есть у каждой
 * дольки, которую вообще видно.
 *
 * ЗАЧЕМ ЗНАЧОК ВНУТРИ. Цвет опознаётся только через легенду — глазу приходится
 * ходить туда-обратно. Значок отвечает на «что это за долька» на месте. Ниже
 * порога он превращается в грязь, поэтому рисуется не везде.
 *
 * Между дольками зазор: соседние цвета различаются по яркости в 1.3–1.7 раза, и
 * без физической границы дольки сливаются — особенно при дальтонизме.
 *
 * Вся арифметика живёт в donutLayout.ts и покрыта тестами; здесь только рисуем.
 */
import type { Side } from "../lib/api.ts";
import type { Colored } from "../lib/category.ts";
import { categoryPaths } from "../lib/categoryIcon.ts";
import { formatMoney } from "../lib/money.ts";
import { donutLayout } from "./donutLayout.ts";

export function Donut({ slices, total, currency, compact, side = "expense" }: {
  slices: Colored[];
  total: string;
  currency: string;
  /** Что в кольце. Подпись под суммой обязана это называть: одинаковый пирог с
   *  надписью «Траты» над доходами — это не оговорка, а неверное число. */
  side?: Side;
  /** Маленький пончик на главной: ни подписей, ни значков — при 130px они
   *  превращаются в грязь. Имена и суммы стоят в легенде рядом. */
  compact?: boolean;
}) {
  const { box, center, radius, stroke, slices: places } =
    donutLayout(slices.map((s) => s.share));
  // Раскладка идёт по долям, данные — по категориям; сшиваем по позиции.
  const parts = slices.map((slice, i) => ({ slice, place: places[i]! }));

  return (
    <svg width="100%" viewBox={`0 0 ${box} ${box}`} role="img"
         style={{ maxWidth: box }}
         aria-label={`${side === "income" ? "Доходы" : "Траты"} `
                   + `${formatMoney(total, currency)} по категориям`}>
      <g transform={`rotate(-90 ${center} ${center})`} fill="none" strokeWidth={stroke}>
        {parts.map(({ slice, place }) => (
          <circle key={slice.name} cx={center} cy={center} r={radius} stroke={slice.color}
                  strokeDasharray={place.arc.dash} strokeDashoffset={place.arc.dashOffset} />
        ))}
      </g>
      {/* В компактном виде (главная, ~118px) кегль 22 при вьюбоксе 264 даёт на
          экране 9.8px, а подпись — 5.8px: ниже пола 11px, который токены
          объявляют минимумом iOS и Material. Сумма там и так стоит в заголовке
          карточки рядом, поэтому в центре её не рисуем. */}
      {compact ? null : (<>
      <text x={center} y={center - 4} textAnchor="middle" fill="var(--text)" fontSize="22"
            fontWeight="700" style={{ fontVariantNumeric: "tabular-nums" }}>
        {formatMoney(total, currency)}
      </text>
      <text x={center} y={center + 16} textAnchor="middle" fill="var(--text-muted)" fontSize="13">
        {side === "income" ? "Доходы" : "Траты"}
      </text>
      </>)}

      {compact ? null : parts.map(({ slice, place }) =>
        place.icon ? (
          // Ink тёмный: на всех наших ярких заливках белый не проходит.
          <g key={`i${slice.name}`}
             transform={`translate(${place.icon.x} ${place.icon.y}) scale(${place.icon.scale})`}
             fill="none" stroke="var(--on-accent)" strokeWidth="2.4"
             strokeLinecap="round" strokeLinejoin="round" opacity="0.85">
            {categoryPaths(slice.name, slice.icon).map((d) => <path key={d} d={d} />)}
          </g>
        ) : null,
      )}

      {compact ? null : parts.map(({ slice, place }) =>
        place.label ? (
          <text key={`l${slice.name}`} x={place.label.x} y={place.label.y}
                textAnchor={place.label.anchor} dominantBaseline={place.label.baseline}
                fill="var(--text-muted)" fontSize="12" fontWeight="600">
            {place.label.text}
          </text>
        ) : null,
      )}
    </svg>
  );
}
